import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Copy, Download, FileText, Mail, Handshake } from "lucide-react";
import { LETTERS, ORGS } from "@/data/partnershipLetters";

const LANGS = ["fr", "en", "es", "nl"];
const FLAG = { fr: "🇫🇷", en: "🇬🇧", es: "🇪🇸", nl: "🇳🇱" };

function fallbackCopy(text) {
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    if (ok) toast.success("Copié dans le presse-papiers");
    else toast.error("Copie impossible");
  } catch {
    toast.error("Copie impossible");
  }
}

function copyText(text) {
  if (navigator.clipboard?.writeText) {
    navigator.clipboard.writeText(text)
      .then(() => toast.success("Copié dans le presse-papiers"))
      .catch(() => fallbackCopy(text));
  } else {
    fallbackCopy(text);
  }
}

function downloadText(filename, text) {
  const blob = new Blob([text], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function Block({ title, Icon, text, filename }) {
  return (
    <div className="glass rounded-2xl border border-primary/15 p-5 sm:p-6">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h4 className="font-display text-lg text-foreground/90 flex items-center gap-2">
          <Icon size={17} className="text-primary" /> {title}
        </h4>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => copyText(text)}
            data-testid={`partner-copy-${filename}`}
            className="inline-flex items-center gap-1.5 text-xs text-foreground/60 hover:text-primary border border-white/10 hover:border-primary/40 rounded-full px-3 py-1.5 transition-colors"
          >
            <Copy size={13} /> Copier
          </button>
          <button
            onClick={() => downloadText(filename, text)}
            data-testid={`partner-download-${filename}`}
            className="inline-flex items-center gap-1.5 text-xs text-foreground/60 hover:text-primary border border-white/10 hover:border-primary/40 rounded-full px-3 py-1.5 transition-colors"
          >
            <Download size={13} /> Télécharger
          </button>
        </div>
      </div>
      <pre className="whitespace-pre-wrap text-sm text-foreground/75 leading-relaxed font-sans bg-black/20 rounded-xl p-4 border border-white/5">{text}</pre>
    </div>
  );
}

export default function PartnersLetterSection() {
  const [lang, setLang] = useState("fr");
  const L = LETTERS[lang];

  const buildAll = () => {
    const parts = [
      `# ${L.fullTitle}`,
      "",
      L.full,
      "",
      "---",
      "",
      `# ${L.shortTitle}`,
      ...ORGS.flatMap((o) => ["", `## ${o.name}`, "", L.short[o.id]]),
    ];
    return parts.join("\n");
  };

  const downloadAll = () => downloadText(`cocoon-partenariats-${lang}.md`, buildAll());
  const copyAll = () => copyText(buildAll());

  return (
    <div className="space-y-6" data-testid="partners-letter-section">
      <div className="glass rounded-2xl border border-primary/20 p-6 frame-pulse">
        <h3 className="font-display text-xl text-gradient-orange flex items-center gap-2 mb-1.5">
          <Handshake size={19} /> Kit partenariats
        </h3>
        <p className="text-foreground/55 text-sm mb-5">
          Lettre type + e-mails de premier contact, prêts à copier ou télécharger. Champs entre [ ] à personnaliser.
          Choisissez la langue puis copiez / téléchargez.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {LANGS.map((lg) => (
            <button
              key={lg}
              onClick={() => setLang(lg)}
              data-testid={`partner-lang-${lg}`}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${
                lang === lg ? "bg-primary/15 text-primary" : "text-foreground/50 hover:text-foreground/80 border border-white/10"
              }`}
            >
              <span>{FLAG[lg]}</span> {LETTERS[lg].langLabel}
            </button>
          ))}
          <button
            onClick={copyAll}
            data-testid="partner-copy-all"
            className="ml-auto inline-flex items-center gap-1.5 text-sm text-foreground/70 border border-white/12 hover:border-primary/40 hover:text-primary rounded-full px-4 py-2 transition-colors"
          >
            <Copy size={15} /> Copier tout
          </button>
          <button
            onClick={downloadAll}
            data-testid="partner-download-all"
            className="inline-flex items-center gap-1.5 text-sm text-primary border border-primary/30 hover:bg-primary/10 rounded-full px-4 py-2 transition-colors"
          >
            <Download size={15} /> Tout télécharger (.md)
          </button>
        </div>
      </div>

      <motion.div key={lang} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="space-y-6">
        <Block
          title={L.fullTitle}
          Icon={FileText}
          text={L.full}
          filename={`cocoon-lettre-${lang}.md`}
        />
        <div>
          <p className="uppercase tracking-[0.22em] text-[0.65rem] text-foreground/40 mb-3">{L.shortTitle}</p>
          <div className="grid lg:grid-cols-2 gap-4">
            {ORGS.map((o) => (
              <Block
                key={o.id}
                title={o.name}
                Icon={Mail}
                text={L.short[o.id]}
                filename={`cocoon-email-${o.id}-${lang}.md`}
              />
            ))}
          </div>
        </div>
      </motion.div>
    </div>
  );
}
